/**
 * IframeBridgeService - Gestisce la comunicazione bidirezionale tra VolontApp e l'app parent MISE
 *
 * Funzionalità:
 * - Invio struttura menu all'app parent
 * - Ricezione comandi di navigazione
 * - Gestione richieste/risposte geolocalizzazione
 */

import { trans } from '@/components/utilities/trans.js'

// Mappa di conversione icone da MDI a UIcons (senza prefisso fi-rr-)
const MDI_TO_UICONS = {
	// Menu principale
	'mdi-view-dashboard-outline': 'apps',
	'mdi-view-dashboard': 'apps',
	'mdi-calendar-today': 'calendar',
	'mdi-calendar': 'calendar',
	'mdi-account-supervisor-outline': 'users',
	'mdi-account-supervisor': 'users',
	'mdi-truck-outline': 'truck-side',
	'mdi-truck': 'truck-side',
	'mdi-equalizer-outline': 'chart-histogram',
	'mdi-equalizer': 'chart-histogram',
	'mdi-book-open-outline': 'book',
	'mdi-book-open': 'book',
	'mdi-clipboard-text': 'clipboard-list',
	'mdi-clipboard-check-outline': 'clipboard-list-check',
	'mdi-clipboard-check': 'clipboard-list-check',
	'mdi-clock-outline': 'clock',
	'mdi-clock': 'clock',
	'mdi-book-check-outline': 'book-alt',
	'mdi-book-check': 'book-alt',
	'mdi-cog-outline': 'settings',
	'mdi-cog': 'settings',
	'mdi-bell-outline': 'bell',
	'mdi-bell': 'bell',
	// Altre icone comuni
	'mdi-account-outline': 'user',
	'mdi-account': 'user',
	'mdi-home-city-outline': 'building',
	'mdi-home-city': 'building',
	'mdi-shield-check-outline': 'shield-check',
	'mdi-shield-check': 'shield-check',
	'mdi-face-agent': 'headset',
	'mdi-exit-to-app': 'sign-out-alt',
	'mdi-login': 'sign-in-alt'
}

/**
 * Converte un'icona MDI nel corrispondente UIcons (senza prefisso)
 * @param {String} mdiIcon - Nome icona MDI (es. 'mdi-calendar-today')
 * @returns {String} - Nome icona UIcons (es. 'calendar')
 */
const convertToUicons = (mdiIcon) => {
	if (!mdiIcon) return 'circle'
	return MDI_TO_UICONS[mdiIcon] || 'circle'
}

// Costanti per i tipi di messaggio
export const MESSAGE_TYPES = {
	// Da VolontApp → App Parent
	MENU_UPDATE: 'VOLONTAPP_MENU_UPDATE',
	REQUEST_LOCATION: 'VOLONTAPP_REQUEST_LOCATION',
	READY: 'VOLONTAPP_READY',
	ROUTE_CHANGED: 'VOLONTAPP_ROUTE_CHANGED',

	// Da App Parent → VolontApp
	NAVIGATE: 'VOLONTAPP_NAVIGATE',
	LOCATION_RESPONSE: 'VOLONTAPP_LOCATION_RESPONSE',
	CHANGE_ORGANIZATION: 'VOLONTAPP_CHANGE_ORGANIZATION'
}

// Mappa delle richieste di geolocalizzazione pendenti
const pendingLocationRequests = new Map()

// Callback per navigazione
let navigationCallback = null

// Callback per cambio organizzazione
let changeOrganizationCallback = null

// Flag per tracciare se il bridge è inizializzato
let isInitialized = false

/**
 * Rileva se siamo in modalità iframe MISE
 * Considera sempre MISE mode come da requisito
 * Nota: Safari con ITP può bloccare l'accesso a window.top da iframe cross-origin
 */
export const isMiseIframe = () => {
	try {
		// Safari può lanciare un'eccezione quando si accede a window.top da iframe cross-origin
		return window.self !== window.top
	} catch (e) {
		// Se c'è un errore di accesso, siamo sicuramente in un iframe cross-origin
		return true
	}
}

/**
 * Genera un ID univoco per le richieste
 */
const generateRequestId = () => {
	return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Invia un messaggio all'app parent
 */
const postToParent = (type, data = {}) => {
	if (!isMiseIframe()) {
		return false
	}

	try {
		window.parent.postMessage({ type, data }, '*')
		return true
	} catch (error) {
		return false
	}
}

/**
 * Gestisce i messaggi ricevuti dall'app parent
 */
const handleParentMessage = function(event) {
	// Safari può ricevere event.data come stringa JSON
	var messageData = event.data
	if (typeof messageData === 'string') {
		try {
			messageData = JSON.parse(messageData)
		} catch (e) {
			return
		}
	}

	// Validazione base del messaggio
	if (!messageData || !messageData.type) {
		return
	}

	var messageType = String(messageData.type)

	// Processa solo messaggi VOLONTAPP_*
	if (messageType.indexOf('VOLONTAPP_') !== 0) {
		return
	}

	if (messageType === MESSAGE_TYPES.NAVIGATE) {
		handleNavigate(messageData.data)
	} else if (messageType === MESSAGE_TYPES.LOCATION_RESPONSE) {
		handleLocationResponse(messageData.data)
	} else if (messageType === MESSAGE_TYPES.CHANGE_ORGANIZATION) {
		handleChangeOrganization(messageData.data)
	}
}

/**
 * Gestisce la navigazione richiesta dall'app parent
 */
const handleNavigate = (data) => {
	if (!data || !data.route) {
		return
	}

	if (navigationCallback) {
		navigationCallback(data.route)
	}
}

/**
 * Gestisce il cambio organizzazione richiesto dall'app parent
 */
const handleChangeOrganization = function(data) {
	if (!data) {
		return
	}

	// Supporta sia organizationId che organization_id (compatibilità)
	var orgId = data.organizationId || data.organization_id || null

	if (orgId === null || orgId === undefined || orgId === '') {
		return
	}

	var orgIdStr = String(orgId)

	if (changeOrganizationCallback) {
		changeOrganizationCallback(orgIdStr)
	}
}

/**
 * Gestisce la risposta di geolocalizzazione dall'app parent
 */
const handleLocationResponse = (data) => {
	if (!data || !data.requestId) {
		return
	}

	const pending = pendingLocationRequests.get(data.requestId)
	if (!pending) {
		return
	}

	// Pulisci il timeout
	if (pending.timeoutId) {
		clearTimeout(pending.timeoutId)
	}

	// Rimuovi dalla mappa
	pendingLocationRequests.delete(data.requestId)

	// Risolvi o rigetta la Promise
	if (data.success && data.position) {
		pending.resolve(data.position)
	} else {
		pending.reject(data.error || { code: 2, message: 'Position unavailable' })
	}
}

/**
 * IframeBridgeService - API pubblica
 */
export const IframeBridgeService = {

	/**
	 * Inizializza il bridge iframe
	 * @param {Function} onNavigate - Callback chiamata quando l'app parent richiede navigazione
	 * @param {Function} onChangeOrganization - Callback chiamata quando l'app parent richiede cambio organizzazione
	 */
	init(onNavigate, onChangeOrganization) {
		if (isInitialized) {
			return
		}

		if (!isMiseIframe()) {
			return
		}

		navigationCallback = onNavigate
		changeOrganizationCallback = onChangeOrganization
		window.addEventListener('message', handleParentMessage)
		isInitialized = true

		// Notifica all'app parent che siamo pronti
		postToParent(MESSAGE_TYPES.READY, { timestamp: Date.now() })
	},

	/**
	 * Distrugge il bridge (cleanup)
	 */
	destroy() {
		if (!isInitialized) return

		window.removeEventListener('message', handleParentMessage)
		navigationCallback = null
		changeOrganizationCallback = null
		pendingLocationRequests.clear()
		isInitialized = false
	},

	/**
	 * Verifica se siamo in modalità iframe
	 */
	isIframeMode: isMiseIframe,

	/**
	 * Invia l'aggiornamento del menu all'app parent
	 * @param {Array} sections - Array delle sezioni/voci menu
	 * @param {Object} organization - Organizzazione corrente
	 * @param {Number} notificationsCount - Numero notifiche non lette
	 * @param {Array} organizations - Lista delle organizzazioni disponibili
	 */
	sendMenuUpdate(sections, organization, notificationsCount = 0, organizations = []) {
		if (!isMiseIframe()) return false

		// Route da escludere in modalità MISE (come in App.vue)
		const excludedRoutes = ['requests', 'organizations', 'privacy']

		// Trasforma le sezioni nel formato richiesto, traducendo le label e convertendo le icone
		const items = sections
			.filter(section => section.status && !excludedRoutes.includes(section.route))
			.map(section => ({
				id: section.module || section.route,
				label: trans(section.trans),
				icon: convertToUicons(section.icon),
				route: `/${section.route}`,
				badge: null
			}))

		// Aggiungi la voce Notifiche (non presente nelle sections standard)
		items.push({
			id: 'notifications',
			label: 'Notifiche',
			icon: 'bell',
			route: '/notifications',
			badge: notificationsCount > 0 ? notificationsCount : null
		})

		// Trasforma le organizzazioni nel formato richiesto
		const orgs = organizations.map(org => ({
			id: org.id,
			name: org.name
		}))

		return postToParent(MESSAGE_TYPES.MENU_UPDATE, {
			items,
			currentOrganization: {
				id: (organization && organization.id) ? organization.id : null,
				name: (organization && organization.name) ? organization.name : null
			},
			organizations: orgs
		})
	},

	/**
	 * Notifica il cambio di route all'app parent
	 * @param {String} route - Path della nuova route
	 * @param {String} name - Nome della route
	 */
	notifyRouteChange(route, name) {
		if (!isMiseIframe()) return false

		return postToParent(MESSAGE_TYPES.ROUTE_CHANGED, {
			route,
			name,
			timestamp: Date.now()
		})
	},

	/**
	 * Richiede le coordinate GPS all'app parent
	 * @param {Object} options - Opzioni geolocalizzazione
	 * @returns {Promise} - Promise che si risolve con le coordinate o rigetta con errore
	 */
	requestLocation(options = {}) {
		return new Promise((resolve, reject) => {
			if (!isMiseIframe()) {
				reject({ code: 0, message: 'Not in iframe mode' })
				return
			}

			const requestId = generateRequestId()
			const timeout = options.timeout || 10000

			// Imposta timeout
			const timeoutId = setTimeout(() => {
				if (pendingLocationRequests.has(requestId)) {
					pendingLocationRequests.delete(requestId)
					reject({ code: 3, message: 'Location request timeout' })
				}
			}, timeout)

			// Salva la richiesta pendente
			pendingLocationRequests.set(requestId, {
				resolve,
				reject,
				timeoutId
			})

			// Invia la richiesta all'app parent
			const sent = postToParent(MESSAGE_TYPES.REQUEST_LOCATION, {
				requestId,
				options: {
					enableHighAccuracy: options.enableHighAccuracy !== false,
					timeout,
					maximumAge: options.maximumAge || 0
				}
			})

			if (!sent) {
				clearTimeout(timeoutId)
				pendingLocationRequests.delete(requestId)
				reject({ code: 0, message: 'Failed to send location request' })
			}
		})
	}
}

export default IframeBridgeService
